import { motion, useReducedMotion } from "framer-motion";
import { Reveal, StaggerGroup, getStaggerChild } from "./motion";
import { InViewTextEffect } from "@/components/ui/text-effect";
import { Check } from "lucide-react";
import { CAPACITY, capacityLabel, capacityCtaLabel } from "../../data/capacity";

const TIERS = [
  {
    name: "Starter",
    price: "$1,800",
    term: "one-time",
    timeline: "4-week slot",
    features: ["5-page website", "Mobile-first build", "Basic on-page SEO", "Contact form"],
  },
  {
    name: "Growth",
    price: "$3,400+",
    term: "one-time",
    timeline: "4-week slot",
    featured: true,
    features: [
      "Everything in Starter",
      "Booking automation",
      "Google Business setup",
      "Analytics dashboard",
    ],
  },
  {
    name: "Custom",
    price: "$5,000+",
    term: "one-time",
    timeline: "4-week slot",
    features: ["Ecommerce / payments", "Scoped individually", "Integrations as needed", "Full build-out"],
  },
];

function scrollToId(id) {
  const el = document.getElementById(id);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -80, duration: 1.2 });
  else el.scrollIntoView({ behavior: "smooth" });
}

export default function Pricing() {
  const reduce = useReducedMotion();
  const staggerChild = getStaggerChild(reduce);
  return (
    <section
      id="pricing"
      className="relative z-10 border-t border-hairline px-5 py-24 md:px-8 md:py-32"
      data-testid="pricing-section"
    >
      <div className="mx-auto max-w-7xl">
        <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
          <div>
            <InViewTextEffect
              as="p"
              preset="blur"
              className="font-mono text-[11px] uppercase tracking-[0.24em] text-rust"
            >
              CH.04 // Rate Card
            </InViewTextEffect>
            <Reveal
              as="h2"
              delay={0.05}
              className="mt-5 max-w-lg font-serif text-4xl font-medium leading-[1.05] tracking-tight text-ink md:text-5xl"
            >
              Flat pricing, mapped up <em className="italic text-pine">front</em>.
            </Reveal>
          </div>
          <Reveal delay={0.1} className="max-w-xs text-sm leading-relaxed text-ink-dim">
            No hourly guessing games. You know the number before we start climbing.
          </Reveal>
        </div>

        {/* asymmetric cards */}
        <StaggerGroup className="mt-16 grid grid-cols-1 items-start gap-6 md:grid-cols-2 lg:grid-cols-3">
          {TIERS.map((t) => (
            <motion.div
              key={t.name}
              variants={staggerChild}
              data-testid={`pricing-card-${t.name.toLowerCase()}`}
              className={
                t.featured
                  ? "relative rounded-lg bg-pine p-8 text-paper md:-translate-y-6 md:shadow-[0_30px_60px_-30px_rgba(47,74,62,0.5)]"
                  : "relative rounded-lg border border-hairline bg-panel/60 p-8"
              }
            >
              {t.featured && (
                <span className="absolute -top-3 left-8 rounded-full bg-paper px-3 py-1 font-mono text-[10px] uppercase tracking-[0.18em] text-pine">
                  Most Booked
                </span>
              )}
              <div className="flex items-center justify-between">
                <h3 className={`font-serif text-3xl ${t.featured ? "text-paper" : "text-ink"}`}>
                  {t.name}
                </h3>
                <span
                  className={`font-mono text-[10px] uppercase tracking-[0.16em] ${
                    t.featured ? "text-paper/90" : "text-ink-dim"
                  }`}
                >
                  {t.timeline}
                </span>
              </div>

              <div className="mt-6 flex items-baseline gap-2">
                <span
                  className={`font-mono text-4xl ${t.featured ? "text-paper" : "text-rust"}`}
                >
                  {t.price}
                </span>
                <span
                  className={`font-mono text-[11px] uppercase tracking-[0.14em] ${
                    t.featured ? "text-paper/90" : "text-ink-dim"
                  }`}
                >
                  {t.term}
                </span>
              </div>

              <ul className="mt-7 space-y-3">
                {t.features.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-sm">
                    <Check
                      className={`mt-0.5 h-4 w-4 flex-none ${
                        t.featured ? "text-paper" : "text-pine"
                      }`}
                    />
                    <span className={t.featured ? "text-paper/80" : "text-ink-dim"}>{f}</span>
                  </li>
                ))}
              </ul>

              <button
                onClick={() => scrollToId("contact")}
                data-testid={`pricing-cta-${t.name.toLowerCase()}`}
                className={`mt-8 w-full rounded-full py-3 font-mono text-[11px] uppercase tracking-[0.16em] transition-transform duration-300 hover:scale-[1.02] active:scale-95 ${
                  t.featured
                    ? "bg-paper text-ink"
                    : "border border-ink/25 text-ink hover:bg-ink hover:text-paper"
                }`}
              >
                Start with {t.name}
              </button>
            </motion.div>
          ))}
        </StaggerGroup>

        {/* Maintenance card */}
        <Reveal delay={0.05} className="mt-6">
          <div
            className="flex flex-col items-start justify-between gap-6 rounded-lg border border-hairline bg-panel/60 p-8 md:flex-row md:items-center"
            data-testid="pricing-card-maintenance"
          >
            <div>
              <div className="flex items-baseline gap-3">
                <h3 className="font-serif text-3xl text-ink">Maintenance</h3>
                <span className="font-mono text-3xl text-rust">$125–200</span>
                <span className="font-mono text-[11px] uppercase tracking-[0.14em] text-ink-dim">
                  / mo · ongoing
                </span>
              </div>
              <p className="mt-3 max-w-xl text-sm leading-relaxed text-ink-dim">
                Hosting, security, uptime monitoring, and 1–2 content changes a month.
                Optional and always opt-in — the site stays sharp long after launch.
              </p>
            </div>
            <button
              onClick={() => scrollToId("contact")}
              data-testid="pricing-cta-maintenance"
              className="flex-none rounded-full border border-ink/25 px-6 py-3 font-mono text-[11px] uppercase tracking-[0.16em] text-ink transition-colors duration-300 hover:bg-ink hover:text-paper"
            >
              Add maintenance
            </button>
          </div>
        </Reveal>

        {/* capacity band */}
        <Reveal delay={0.05} className="mt-10">
          <div
            className="flex flex-col items-start justify-between gap-6 rounded-lg border border-hairline bg-panel px-8 py-9 text-ink md:flex-row md:items-center"
            data-testid="capacity-band"
          >
            <div className="flex items-center gap-6">
              {/* capacity gauge */}
              <div className="flex flex-col items-center rounded-md border border-hairline px-5 py-3">
                <span className="flex items-center gap-2 font-mono text-2xl text-ink">
                  <span className="h-2.5 w-2.5 rounded-full bg-rust" />
                  {CAPACITY.slotsFilled} / {CAPACITY.slotsTotal}
                </span>
                <span className="mt-1 font-mono text-[9px] uppercase tracking-[0.22em] text-ink-dim">
                  Slot · {capacityLabel}
                </span>
              </div>
              <p className="max-w-md font-serif text-2xl leading-snug md:text-3xl">
                One build a month. When the slot's taken,{" "}
                <em className="italic text-pine">it's taken.</em>
              </p>
            </div>
            <button
              onClick={() => scrollToId("contact")}
              data-testid="capacity-cta"
              className="flex-none rounded-full bg-pine px-7 py-3.5 font-mono text-[11px] uppercase tracking-[0.16em] text-paper transition-transform duration-300 hover:scale-[1.03] active:scale-95"
            >
              {capacityCtaLabel}
            </button>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
